import { AsyncPipe } from '@angular/common';
import {
  CdkDrag,
  CdkDragHandle,
  CdkDropList,
  type CdkDragDrop,
} from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { type ObjectResultsTableColumnId } from '@bizzdesign/data-access-search';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import {
  DefaultDialogSize,
  DialogProperties,
  type BDMenuItem,
} from '@bizzdesign/shared/models';
import {
  HighlightTextPipe,
  TranslateMenuItemsPipe,
} from '@bizzdesign/shared/pipes';
import { DialogComponent } from '@bizzdesign/ui-components';
import { BdMenuItemComponent } from '@bizzdesign/ui-menu';
import { TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { type TieredMenu, TieredMenuModule } from 'primeng/tieredmenu';
import { TooltipModule } from 'primeng/tooltip';
import { combineLatest, map, type Observable } from 'rxjs';
import {
  groupObjectResultsColumns,
  hideColumn,
  hideColumns,
  moveColumn,
  reorderColumnGroup,
  showColumn,
  showColumns,
  SYSTEM_COLUMN_GROUP_KEY,
  toggleColumnPin,
  type ColumnMoveDirection,
  type ObjectResultsColumn,
  type ObjectResultsColumnCatalogue,
  type ObjectResultsColumnDefinition,
  type ObjectResultsColumnGroup,
  type SelectedColumnsGroup,
} from '../../utils/object-results-columns.utils';

/**
 * One group of available columns, with everything the dialog renders for it.
 *
 * @property filteredColumns - The group's columns the current filter keeps.
 * @property selectedColumnCount - How many of the group's columns are shown.
 * @property allSelected - Whether every column of the group is shown.
 * @property indeterminate - Whether the group is partly shown.
 * @property expanded - Whether the group's rows are visible.
 */
type AvailableColumnGroupView = ObjectResultsColumnGroup & {
  readonly filteredColumns: readonly ObjectResultsColumnDefinition[];
  readonly selectedColumnCount: number;
  readonly allSelected: boolean;
  readonly indeterminate: boolean;
  readonly expanded: boolean;
};

/**
 * Lets the user choose, order, and pin the columns of the object search results
 * table, and confirms or discards the whole set of changes at once.
 *
 * @remarks
 * Purely presentational: it edits a draft copy of the column layout and emits it
 * on confirm, so the caller owns the persisted state and Cancel needs no
 * snapshot.
 */
@Component({
  selector: 'bd-manage-object-columns-dialog',
  imports: [
    AsyncPipe,
    CdkDrag,
    CdkDragHandle,
    CdkDropList,
    FormsModule,
    SharedI18nModule,
    ButtonModule,
    CheckboxModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    TooltipModule,
    TieredMenuModule,
    BdMenuItemComponent,
    DialogComponent,
    HighlightTextPipe,
    TranslateMenuItemsPipe,
  ],
  templateUrl: './manage-object-columns-dialog.component.html',
  styleUrl: './manage-object-columns-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageObjectColumnsDialogComponent {
  private readonly translocoService = inject(TranslocoService);

  readonly visible = input(false);
  readonly columns = input.required<ObjectResultsColumn[]>();
  readonly catalogue = input.required<ObjectResultsColumnCatalogue>();

  readonly confirm = output<ObjectResultsColumn[]>();
  readonly dismiss = output<void>();

  readonly draftColumns = linkedSignal(() => this.columns());
  readonly availableColumnsSearchActive = signal(false);
  readonly availableColumnsSearchTerm = signal('');
  readonly selectedColumnsSearchActive = signal(false);
  readonly selectedColumnsSearchTerm = signal('');
  // Only the built-in group starts open: a type with many attribute categories
  // would otherwise fill the panel before the user has chosen one.
  readonly expandedGroupKeys = signal<ReadonlySet<string>>(
    new Set([SYSTEM_COLUMN_GROUP_KEY]),
  );

  readonly dialogProperties$: Observable<DialogProperties> = combineLatest([
    this.selectTranslation$('manageTable'),
    this.selectTranslation$('applyChanges'),
    this.selectTranslation$('cancel'),
  ]).pipe(
    map(
      ([headerTitle, footerConfirmButtonLabel, footerDismissButtonLabel]) =>
        new DialogProperties({
          defaultSize: DefaultDialogSize.LARGE,
          defaultHeader: { headerTitle },
          defaultFooter: {
            footerConfirmButtonLabel,
            footerDismissButtonLabel,
          },
        }),
    ),
  );

  readonly pinnedColumns = computed(() =>
    this.draftColumns().filter((column) => column.pinned),
  );
  readonly unpinnedColumns = computed(() =>
    this.draftColumns().filter((column) => !column.pinned),
  );
  private readonly columnGroups = computed(() =>
    groupObjectResultsColumns(this.catalogue()),
  );
  readonly availableColumnGroups = computed<AvailableColumnGroupView[]>(() => {
    const searchTerm = this.availableColumnsSearchTerm();
    const expandedGroupKeys = this.expandedGroupKeys();
    const visibleColumnIds = new Set(this.draftColumns().map(({ id }) => id));
    return this.columnGroups().map((group) => {
      const selectedColumnCount = group.columns.filter(({ id }) =>
        visibleColumnIds.has(id),
      ).length;
      return {
        ...group,
        filteredColumns: this.filterColumns(group.columns, searchTerm),
        selectedColumnCount,
        allSelected: selectedColumnCount === group.columns.length,
        indeterminate:
          selectedColumnCount > 0 && selectedColumnCount < group.columns.length,
        expanded: expandedGroupKeys.has(group.key),
      };
    });
  });
  readonly hasMatchingAvailableColumns = computed(() =>
    this.availableColumnGroups().some(
      ({ filteredColumns }) => filteredColumns.length > 0,
    ),
  );
  readonly filteredPinnedColumns = computed(() =>
    this.filterColumns(this.pinnedColumns(), this.selectedColumnsSearchTerm()),
  );
  readonly filteredUnpinnedColumns = computed(() =>
    this.filterColumns(
      this.unpinnedColumns(),
      this.selectedColumnsSearchTerm(),
    ),
  );

  readonly selectedColumnGroups = computed(() => [
    {
      key: 'pinned' as SelectedColumnsGroup,
      labelKey: 'pinnedColumns',
      columns: this.pinnedColumns(),
      filteredColumns: this.filteredPinnedColumns(),
    },
    {
      key: 'unpinned' as SelectedColumnsGroup,
      labelKey: 'unpinnedColumns',
      columns: this.unpinnedColumns(),
      filteredColumns: this.filteredUnpinnedColumns(),
    },
  ]);

  readonly selectedColumnMenuItemsById = computed(() => {
    const columns = this.draftColumns();
    const menuItemsById = new Map<ObjectResultsTableColumnId, BDMenuItem[]>();
    for (const column of columns) {
      const columnsInGroup = columns.filter(
        ({ pinned }) => pinned === column.pinned,
      );
      const columnIndex = columnsInGroup.findIndex(
        ({ id }) => id === column.id,
      );
      menuItemsById.set(column.id, [
        {
          id: column.pinned ? 'unpin-column' : 'pin-column',
          labelKey: column.pinned
            ? 'searchResults.unpinColumn'
            : 'searchResults.pinColumn',
          tScope: 'featureSearch',
          icon: column.pinned ? 'ui-pin-solid' : 'ui-pin',
          command: () => this.toggleColumnPin(column.id),
        },
        {
          separator: true,
        },
        {
          id: 'move-column-up',
          labelKey: 'searchResults.moveColumnUp',
          tScope: 'featureSearch',
          icon: 'ui-arrow-up',
          disabled: columnIndex === 0,
          command: () => this.moveColumn(column.id, 'left'),
        },
        {
          id: 'move-column-down',
          labelKey: 'searchResults.moveColumnDown',
          tScope: 'featureSearch',
          icon: 'ui-arrow-down',
          disabled: columnIndex === columnsInGroup.length - 1,
          command: () => this.moveColumn(column.id, 'right'),
        },
        {
          separator: true,
        },
        {
          id: 'remove-column',
          labelKey: 'searchResults.removeColumn',
          tScope: 'featureSearch',
          icon: 'ui-hidden',
          disabled: columns.length === 1 || !column.canHide,
          command: () => this.hideColumn(column.id),
        },
      ]);
    }
    return menuItemsById;
  });

  private openMenu: TieredMenu | undefined = undefined;

  /**
   * Reports whether a column is currently part of the draft layout.
   *
   * @param columnId - Identifier of the column to check.
   * @returns `true` when the column is shown.
   */
  isColumnVisible(columnId: ObjectResultsTableColumnId): boolean {
    return this.draftColumns().some(({ id }) => id === columnId);
  }

  /**
   * Shows or hides a column from the available-columns list.
   *
   * @param columnId - Identifier of the column that was toggled.
   * @param visible - Whether the column should be shown.
   */
  onAvailableColumnChange(
    columnId: ObjectResultsTableColumnId,
    visible: boolean,
  ): void {
    this.draftColumns.update((columns) =>
      visible
        ? showColumn(columns, columnId, this.catalogue())
        : hideColumn(columns, columnId),
    );
  }

  /**
   * Shows or hides every column of one group.
   *
   * @param group - Group whose columns were toggled.
   * @param visible - Whether the group's columns should be shown.
   */
  toggleColumnGroup(group: ObjectResultsColumnGroup, visible: boolean): void {
    const columnIds = group.columns.map(({ id }) => id);
    this.draftColumns.update((columns) =>
      visible
        ? showColumns(columns, columnIds, this.catalogue())
        : hideColumns(columns, columnIds),
    );
  }

  /**
   * Toggles the search field above the available-columns list.
   */
  toggleAvailableColumnsSearch(): void {
    const active = !this.availableColumnsSearchActive();
    this.availableColumnsSearchActive.set(active);
    if (!active) {
      this.searchAvailableColumns('');
    }
  }

  /**
   * Filters the available columns, opening the groups that hold a match.
   *
   * @param searchTerm - What the user typed, or an empty string to stop
   * filtering.
   *
   * @remarks
   * The expanded groups are written here rather than derived from the search
   * term, so that expanding and collapsing keeps working while a filter is set,
   * the way the details drawer's attribute search behaves. Clearing the term
   * goes back to the built-in group alone, not to whatever was open before.
   */
  searchAvailableColumns(searchTerm: string): void {
    this.availableColumnsSearchTerm.set(searchTerm);
    this.expandedGroupKeys.set(
      searchTerm.trim()
        ? new Set(
            this.columnGroups()
              .filter(
                ({ columns }) =>
                  this.filterColumns(columns, searchTerm).length > 0,
              )
              .map(({ key }) => key),
          )
        : new Set([SYSTEM_COLUMN_GROUP_KEY]),
    );
  }

  /**
   * Toggles the search field above the selected-columns list.
   */
  toggleSelectedColumnsSearch(): void {
    const active = !this.selectedColumnsSearchActive();
    this.selectedColumnsSearchActive.set(active);
    if (!active) {
      this.selectedColumnsSearchTerm.set('');
    }
  }

  /**
   * Expands or collapses one group of available columns.
   *
   * @param groupKey - Key of the group that was toggled.
   */
  toggleColumnGroupExpanded(groupKey: string): void {
    this.expandedGroupKeys.update((expandedGroupKeys) => {
      const nextKeys = new Set(expandedGroupKeys);
      if (!nextKeys.delete(groupKey)) {
        nextKeys.add(groupKey);
      }
      return nextKeys;
    });
  }

  /**
   * Pins or unpins a column in the draft layout.
   *
   * @param columnId - Identifier of the column to pin or unpin.
   */
  toggleColumnPin(columnId: ObjectResultsTableColumnId): void {
    this.draftColumns.update((columns) => toggleColumnPin(columns, columnId));
  }

  /**
   * Moves a column one slot inside its pinned or unpinned group.
   *
   * @param columnId - Identifier of the column to move.
   * @param direction - Whether to move it towards the start or the end.
   */
  moveColumn(
    columnId: ObjectResultsTableColumnId,
    direction: ColumnMoveDirection,
  ): void {
    this.draftColumns.update((columns) =>
      moveColumn(columns, columnId, direction),
    );
  }

  /**
   * Removes a column from the draft layout.
   *
   * @param columnId - Identifier of the column to remove.
   */
  hideColumn(columnId: ObjectResultsTableColumnId): void {
    this.draftColumns.update((columns) => hideColumn(columns, columnId));
  }

  /**
   * Applies a drag-and-drop reorder within one group of selected columns.
   *
   * @param event - Drop event emitted by the CDK drop list.
   * @param group - Group the drop list represents.
   */
  onSelectedColumnsDrop(
    event: CdkDragDrop<ObjectResultsColumn[]>,
    group: SelectedColumnsGroup,
  ): void {
    if (event.previousContainer !== event.container) {
      return;
    }

    const isPinnedGroup = group === 'pinned';
    const visibleGroupColumns = isPinnedGroup
      ? this.filteredPinnedColumns()
      : this.filteredUnpinnedColumns();
    const draggedColumn = visibleGroupColumns[event.previousIndex];
    if (!draggedColumn) {
      return;
    }

    const reorderedGroupColumns = reorderColumnGroup(
      isPinnedGroup ? this.pinnedColumns() : this.unpinnedColumns(),
      visibleGroupColumns,
      draggedColumn.id,
      event.currentIndex,
    );

    this.draftColumns.set(
      isPinnedGroup
        ? [...reorderedGroupColumns, ...this.unpinnedColumns()]
        : [...this.pinnedColumns(), ...reorderedGroupColumns],
    );
  }

  /**
   * Opens one column's action menu, closing any menu already open.
   *
   * @param event - Click event that opened the menu.
   * @param actionMenu - Menu to toggle.
   */
  onSelectedColumnMenuClick(event: Event, actionMenu: TieredMenu): void {
    event.stopPropagation();
    if (this.openMenu && this.openMenu !== actionMenu) {
      this.openMenu.hide();
    }
    actionMenu.toggle(event);
    this.openMenu = actionMenu.visible ? actionMenu : undefined;
  }

  /**
   * Swallows clicks on disabled menu items so the menu stays open.
   *
   * @param event - Click event raised by the menu item.
   * @param item - Menu item that was clicked.
   */
  onMenuItemClick(event: Event, item: BDMenuItem): void {
    if (item.disabled) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /**
   * Emits the edited column layout.
   */
  onConfirm(): void {
    this.confirm.emit(this.draftColumns());
  }

  /**
   * Discards the edited column layout.
   */
  onDismiss(): void {
    this.dismiss.emit();
  }

  private filterColumns<T extends ObjectResultsColumnDefinition>(
    columns: readonly T[],
    searchTerm: string,
  ): readonly T[] {
    const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase();
    if (!normalizedSearchTerm) {
      return columns;
    }
    return columns.filter((column) =>
      this.columnLabel(column)
        .toLocaleLowerCase()
        .includes(normalizedSearchTerm),
    );
  }

  private columnLabel(column: ObjectResultsColumnDefinition): string {
    return column.kind === 'system'
      ? this.translocoService.translate(
          `featureSearch.searchResults.${column.headerKey}`,
        )
      : column.label;
  }

  private selectTranslation$(key: string): Observable<string> {
    return this.translocoService.selectTranslate(
      `searchResults.${key}`,
      undefined,
      'featureSearch',
    );
  }
}
